import { useEffect, useRef } from 'react';

import { useSubstitutionFlowStatusChipLang } from './useSubstitutionFlowStatusChipLang';

import type { Props } from '../substitutionFlowStatusChip.model';

export function useSubstitutionFlowStatusChip(props: _Props) {
  const { status, deletedAt } = props;

  const countdownRef = useRef<HTMLSpanElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isRender = !!status;

  const showCountdown = status === 'awaiting' && Boolean(deletedAt);

  const lang = useSubstitutionFlowStatusChipLang({ status });

  useEffect(() => {
    return () => {
      countdownRef.current?.remove();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (showCountdown && deletedAt && countdownRef.current) {
      updateChipLabel(countdownRef.current, deletedAt);
      intervalRef.current = setInterval(() => {
        if (countdownRef.current) {
          updateChipLabel(countdownRef.current, deletedAt);
        }
      }, 1_000);
    } else if (countdownRef.current) countdownRef.current.textContent = '';
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [showCountdown, deletedAt, lang.label]);

  return {
    lang,
    isRender,
    countdownRef,
    showCountdown,
  };
}

type _Props = Pick<Props, 'status' | 'deletedAt'>;

function updateChipLabel(element: HTMLSpanElement, deletedAt: Date): void {
  element.textContent = calculateTimeLeft(deletedAt);
}

function calculateTimeLeft(deletedAtDate: Date): string {
  const timeDiff = deletedAtDate.getTime() - Date.now();
  if (timeDiff <= 0) return '0:00';
  const minutes = Math.floor(timeDiff / (60 * 1_000));
  const seconds = Math.floor((timeDiff % (60 * 1_000)) / 1_000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
